import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminLoginPayload, AdminUser } from '@vmgate/common';
import { adminApi, ApiError, registerUnauthorizedHandler } from '@vmgate/common';

interface AuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  login: (credentials: AdminLoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const current = await adminApi.getCurrentUser();
      setUser(current);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
      } else {
        console.warn('Failed to refresh admin session', error);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const bootstrap = async () => {
      try {
        await refresh();
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    bootstrap();
    return () => {
      isMounted = false;
    };
  }, [refresh]);

  const login = useCallback(async (credentials: AdminLoginPayload) => {
    setLoading(true);
    try {
      const loggedIn = await adminApi.login(credentials);
      setUser(loggedIn);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      console.warn('Failed to logout admin', error);
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      logout,
      refresh
    }),
    [user, loading, login, logout, refresh]
  );

  useEffect(() => {
    const unsubscribe = registerUnauthorizedHandler(() => {
      setUser(null);
      sessionStorage.setItem('vmgate:adminAuthMessage', 'Session expired. Please sign in again.');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    });
    return unsubscribe;
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}

